import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'

/**
 * The one data-fetching hook.
 *
 * Deliberately not a query library: this app has nine screens and a handful of endpoints, and
 * a cache layer would be more code than the code it manages. `reload` re-runs the request
 * after a mutation.
 */
export type AsyncState<T> = {
  data: T | null
  error: unknown
  loading: boolean
  reload: () => void
}

export function useApi<T>(path: string | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(path !== null)

  /**
   * Bumped on every request. A response whose generation is stale, because the merchant
   * navigated, or `reload` fired again, is discarded rather than overwriting the screen
   * they are now looking at.
   */
  const generation = useRef(0)

  const load = useCallback(() => {
    if (path === null) return
    const current = ++generation.current

    setLoading(true)
    setError(null)

    api
      .get<T>(path)
      .then((result) => {
        if (generation.current === current) setData(result)
      })
      .catch((err) => {
        if (generation.current === current) setError(err)
      })
      .finally(() => {
        if (generation.current === current) setLoading(false)
      })
  }, [path])

  useEffect(load, [load])

  return { data, error, loading, reload: load }
}
